/**
 * 多版本菜单批 L2（后端仓 plan `docs/plans/2026-10-28-multi-menu-link-only-multi-table-plan.md` §2.3 / §2.4，调研 D6 / D7 = A）：
 * 点单链接的桌号表（管理端链接卡 At a table → Some tables）。
 *
 * Some tables 链接的地址与 Any table 一样（`/v/<店>/t/n-t?qr=1&menuLink=<码>`），不带桌号；后端在 get-menu / get-product-list 回包的
 * `resolvedMenu` 里，**这次是按链接选中、且链接有桌号表**时多一个 `linkTables: string[]`（名册原名），其它情况没有这个键。
 * 购物车桌号下拉只列「链接桌号表 ∩ 门店名册」（按名册顺序）；交集为空或没有桌号表 ⇒ 全名册（= 今天 Any table 的样子）。
 * 只限制下拉，后端不按桌号拒单（D8 = A）。
 *
 * ## 只放内存
 * 记在本模块的一个变量里（按门店 + 链接码，一次只记一份）：**不进 vuex**（根 state 被 vuex-persist 整份写进 `localStorage.vuex`）、
 * 不进 localStorage / sessionStorage / cookie。码本身也只从地址现读（`menuLink.js`），这里只拿它比对「是不是同一张码」。
 * 整页刷新后内存是空的 ⇒ 购物车用与首页同一份 context 再调一次 get-menu 取表（`loadLinkTables`）；失败 ⇒ 全名册。
 *
 * 本文件不 import 任何模块。
 */

/** 一次只记一份：{ venueId, code, tables } */
let remembered = null

/** 记下的表变了要通知谁（已挂着的购物车：「菜单刚更新」重新解析时购物车不会重新挂载） */
const listeners = new Set()

function notify() {
  listeners.forEach(fn => fn())
}

/**
 * 订阅「记下的桌号表变了」（记下 / 清掉都算）。返回取消订阅的函数。
 * @param {Function} fn
 * @returns {Function}
 */
export function onLinkTablesChange(fn) {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

/**
 * get-menu / get-product-list 回包里的链接桌号表。没有 / 形状不对 ⇒ []。
 * 只留非空字符串，顺序原样（后端已按名册原名去重）。
 */
export function linkTablesOf(data) {
  const resolved = data && data.resolvedMenu
  const tables = resolved && resolved.linkTables
  if (!Array.isArray(tables)) return []
  return tables.filter(name => typeof name === 'string' && name !== '')
}

/** 清掉记下的桌号表 */
export function forgetLinkTables() {
  remembered = null
  notify()
}

/**
 * 首页 get-menu 回来后调用：这次带了码且有回包 ⇒ 记下这条链接的桌号表（没有 linkTables 记成 []：「已知没有」，购物车不再补取）；
 * 没带码 / 没有回包（请求失败）⇒ 清掉。
 * @param {String} venueId store 里的门店 id
 * @param {String} [code] 这次 context 里的点单链接码
 * @param {Object} [data] get-menu 的 data
 */
export function rememberLinkTables(venueId, code, data) {
  if (!venueId || !code || !data || typeof data !== 'object') {
    forgetLinkTables()
    return
  }
  remembered = { venueId, code, tables: linkTablesOf(data) }
  notify()
}

/**
 * 记下的桌号表（副本）。不是同一家店、同一张码 ⇒ undefined（不知道）；记过但没有 ⇒ []。
 */
export function linkTablesFor(venueId, code) {
  if (!remembered || !venueId || !code) return undefined
  if (remembered.venueId !== venueId || remembered.code !== code) return undefined
  return remembered.tables.slice()
}

/**
 * 购物车下拉的选项：名册里名字在链接桌号表里的那几张，按名册顺序。
 * 没有桌号表 / 交集为空 / 名册不是数组 ⇒ 名册原样（同一个数组）。
 * @param {Array<{name: String}>} roster 门店桌号表（get-venue 的 `tables`）
 * @param {String[]} [linkTables]
 */
export function linkTableOptions(roster, linkTables) {
  if (!Array.isArray(roster) || !Array.isArray(linkTables) || !linkTables.length) return roster
  const wanted = new Set(linkTables)
  const options = roster.filter(item => item && wanted.has(item.name))
  return options.length ? options : roster
}

/**
 * 购物车用：内存里有这张码的桌号表就直接用；没有（整页刷新）⇒ `fetchMenu()` 补取一次并记下。
 * 补取失败 / 没有回包 ⇒ []（全名册），不记。没有码 / 门店 ⇒ []，不发请求。
 * @param {Object} input
 * @param {String} input.venueId
 * @param {String} [input.code]
 * @param {Function} input.fetchMenu 返回 get-menu data 的 Promise（调用方用与首页同一份 context 拼请求）
 * @returns {Promise<String[]>}
 */
export async function loadLinkTables({ venueId, code, fetchMenu } = {}) {
  if (!venueId || !code) return []
  const known = linkTablesFor(venueId, code)
  if (known) return known
  let data
  try {
    data = await fetchMenu()
  } catch (error) {
    return []
  }
  if (!data || typeof data !== 'object') return []
  rememberLinkTables(venueId, code, data)
  return linkTablesOf(data)
}
